import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { db, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Search, CheckCircle, XCircle, Clock, Building, MapPin, X, Image as ImageIcon, PauseCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import ImageGallery from '../components/ImageGallery';
import Pagination from '../components/Pagination';

const PropertyRequests = () => {
  const { colors } = useTheme();
  const { currentUser } = useAuth();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [propertyToReject, setPropertyToReject] = useState(null);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [galleryProperty, setGalleryProperty] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    filterProperties();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, searchTerm, filterStatus]);

  const fetchProperties = async () => {
    try {
      const propertiesQuery = query(collection(db, 'properties'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(propertiesQuery);
      const propertiesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProperties(propertiesData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to fetch property requests');
      setLoading(false);
    }
  };

  const filterProperties = () => {
    let filtered = properties;

    if (searchTerm) {
      filtered = filtered.filter(property =>
        property.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.address?.city?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(property => {
        const status = property.approvalStatus || 'pending';
        return status === filterStatus;
      });
    }

    setFilteredProperties(filtered);
    setCurrentPage(1);
  };

  const approveProperty = async (propertyId) => {
    try {
      const propertyRef = doc(db, 'properties', propertyId);
      await updateDoc(propertyRef, {
        approvalStatus: 'approved',
        approvedAt: new Date(),
        approvedBy: currentUser?.uid || 'admin',
        updatedAt: new Date()
      });

      setProperties(properties.map(property =>
        property.id === propertyId
          ? { ...property, approvalStatus: 'approved', approvedAt: new Date() }
          : property
      ));

      toast.success('Property approved successfully');
      setSelectedProperty(null);
    } catch (error) {
      console.error('Error approving property:', error);
      toast.error('Failed to approve property');
    }
  };

  const rejectProperty = async () => {
    if (!propertyToReject) return;

    try {
      const propertyRef = doc(db, 'properties', propertyToReject.id);
      await updateDoc(propertyRef, {
        approvalStatus: 'rejected',
        rejectionReason: rejectionReason || 'Does not meet listing requirements',
        updatedAt: new Date()
      });

      setProperties(properties.map(property =>
        property.id === propertyToReject.id
          ? { ...property, approvalStatus: 'rejected', rejectionReason }
          : property
      ));

      toast.success('Property rejected');
      setShowRejectModal(false);
      setPropertyToReject(null);
      setRejectionReason('');
      setSelectedProperty(null);
    } catch (error) {
      console.error('Error rejecting property:', error);
      toast.error('Failed to reject property');
    }
  };

  const suspendProperty = async (propertyId) => {
    try {
      const propertyRef = doc(db, 'properties', propertyId);
      await updateDoc(propertyRef, {
        approvalStatus: 'suspended',
        updatedAt: new Date()
      });

      setProperties(properties.map(property =>
        property.id === propertyId
          ? { ...property, approvalStatus: 'suspended' }
          : property
      ));

      toast.success('Property suspended');
      setSelectedProperty(null);
    } catch (error) {
      console.error('Error suspending property:', error);
      toast.error('Failed to suspend property');
    }
  };

  const revertToPending = async (propertyId) => {
    try {
      const propertyRef = doc(db, 'properties', propertyId);
      await updateDoc(propertyRef, {
        approvalStatus: 'pending',
        rejectionReason: null,
        updatedAt: new Date()
      });

      setProperties(properties.map(property =>
        property.id === propertyId
          ? { ...property, approvalStatus: 'pending', rejectionReason: null }
          : property
      ));

      toast.success('Property reverted to pending review');
      setSelectedProperty(null);
    } catch (error) {
      console.error('Error reverting property:', error);
      toast.error('Failed to revert property');
    }
  };

  const openImageGallery = async (property) => {
    try {
      const getPropertyDocumentAccess = httpsCallable(functions, 'getPropertyDocumentAccess');
      const result = await getPropertyDocumentAccess({ propertyId: property.id });
      setGalleryProperty({
        ...property,
        verificationDocUrls: result.data?.verificationDocs || [],
        subleaseConsentDocUrl: result.data?.subleaseConsentDoc || null,
      });
    } catch (error) {
      console.error('Error fetching secure property document URLs:', error);
      setGalleryProperty({
        ...property,
        verificationDocUrls: [],
        subleaseConsentDocUrl: null,
      });
      toast.error('Could not load secure property documents.');
    }
    setShowImageGallery(true);
  };

  const closeImageGallery = () => {
    setShowImageGallery(false);
    setGalleryProperty(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: colors.warningGhost, color: colors.warning, icon: Clock, text: 'Pending Review' },
      approved: { bg: colors.successGhost, color: colors.success, icon: CheckCircle, text: 'Approved' },
      rejected: { bg: colors.errorGhost, color: colors.error, icon: XCircle, text: 'Rejected' },
      suspended: { bg: colors.primaryGhost, color: colors.textMuted, icon: PauseCircle, text: 'Suspended' }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <div 
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase shadow-sm"
        style={{ backgroundColor: badge.bg, color: badge.color, border: `1px solid ${badge.color}30`, backdropFilter: 'blur(8px)' }}
      >
        <Icon className="w-3.5 h-3.5" />
        {badge.text}
      </div>
    );
  };

  const stats = {
    pending: properties.filter(p => (p.approvalStatus || 'pending') === 'pending').length,
    approved: properties.filter(p => p.approvalStatus === 'approved').length,
    rejected: properties.filter(p => p.approvalStatus === 'rejected').length,
    suspended: properties.filter(p => p.approvalStatus === 'suspended').length,
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="skeleton h-8 w-48 mb-2" style={{ backgroundColor: colors.border }} />
          <div className="skeleton h-4 w-72" style={{ backgroundColor: colors.border }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="rounded-2xl p-4 h-[100px]" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
               <div className="skeleton h-4 w-24 mb-4" style={{ backgroundColor: colors.border }} />
               <div className="skeleton h-7 w-12" style={{ backgroundColor: colors.border }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    { id: 'pending', label: 'Pending Review', value: stats.pending, icon: Clock, color: colors.warning, gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' },
    { id: 'approved', label: 'Approved', value: stats.approved, icon: CheckCircle, color: colors.success, gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' },
    { id: 'rejected', label: 'Rejected', value: stats.rejected, icon: XCircle, color: colors.error, gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' },
    { id: 'suspended', label: 'Suspended', value: stats.suspended, icon: PauseCircle, color: colors.textSecondary, gradient: 'linear-gradient(135deg, #64748B 0%, #475569 100%)' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            Property Requests
          </h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            Review, approve, and manage new listing submissions.
          </p>
        </div>
      </div>

      {/* Stats row as visual filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {statCards.map((stat, i) => {
          const isActive = filterStatus === stat.id;
          return (
            <div
              key={stat.id}
              className={`card-premium p-4 cursor-pointer transition-all ${isActive ? 'ring-2' : ''} opacity-0 animate-fadeInScale stagger-${i+1}`}
              style={{
                backgroundColor: isActive ? `${stat.color}10` : colors.card,
                border: `1px solid ${isActive ? stat.color : colors.border}`,
                boxShadow: isActive ? `0 4px 20px ${stat.color}20` : undefined,
                ringColor: isActive ? stat.color : 'transparent'
              }}
              onClick={() => setFilterStatus(stat.id)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[12px] font-semibold uppercase tracking-wider mb-1" style={{ color: isActive ? stat.color : colors.textSecondary }}>{stat.label}</p>
                  <p className="text-2xl font-bold" style={{ color: colors.text }}>{stat.value}</p>
                </div>
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                  style={{ background: stat.gradient }}
                >
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Search Bar */}
      <div 
        className="card-premium p-4"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
             <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[18px] h-[18px]" style={{ color: colors.textMuted }} />
             <input
               type="text"
               placeholder="Search by title, location..."
               className="w-full pl-11 pr-4 py-2.5 rounded-xl text-[14px] focus:outline-none transition-all duration-200"
               style={{
                 backgroundColor: colors.backgroundSecondary,
                 border: `1px solid ${colors.border}`,
                 color: colors.text
               }}
               onFocus={(e) => {
                 e.target.style.borderColor = colors.borderActive;
                 e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`;
               }}
               onBlur={(e) => {
                 e.target.style.borderColor = colors.border;
                 e.target.style.boxShadow = 'none';
               }}
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
             />
          </div>
          <button
             onClick={() => setFilterStatus('all')}
             className="px-5 py-2.5 rounded-xl text-[13px] font-semibold transition-all hover:shadow-md"
             style={{
               backgroundColor: filterStatus === 'all' ? colors.primary : colors.backgroundSecondary,
               color: filterStatus === 'all' ? '#FFFFFF' : colors.text,
               border: `1px solid ${filterStatus === 'all' ? colors.primaryDark : colors.border}`
             }}
          >
             Show All
          </button>
        </div>
      </div>

      {/* Properties Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-6">
        {filteredProperties.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((property, idx) => (
           <div
             key={property.id}
             className={`card-premium overflow-hidden transition-all cursor-pointer opacity-0 animate-fadeInScale stagger-${Math.min(idx+1, 6)}`}
             style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
             onClick={() => setSelectedProperty(property)}
           >
             <div className="relative h-52 group overflow-hidden">
               {property.imageUrls && property.imageUrls.length > 0 ? (
                 <img
                   src={property.imageUrls[0]}
                   alt={property.title}
                   className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                 />
               ) : (
                 <div
                   className="w-full h-full flex items-center justify-center transition-transform duration-500 group-hover:scale-105"
                   style={{ backgroundColor: colors.backgroundSecondary }}
                 >
                   <Building className="w-10 h-10" style={{ color: colors.textMuted }} />
                 </div>
               )}
               {/* Overlay gradient */}
               <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
               
               {/* Badges on image */}
               <div className="absolute top-4 right-4 z-10">
                 {getStatusBadge(property.approvalStatus || 'pending')}
               </div>

               {/* Image Gallery Button */}
               {(property.imageUrls?.length > 0 || property.verificationDocUrls?.length > 0 || property.subleaseConsentDocUrl) && (
                 <button
                   onClick={(e) => {
                     e.stopPropagation();
                     openImageGallery(property);
                   }}
                   className="absolute bottom-4 right-4 p-2.5 rounded-xl backdrop-blur-md transition-all hover:scale-105 z-10"
                   style={{
                     backgroundColor: 'rgba(255, 255, 255, 0.15)',
                     border: '1px solid rgba(255, 255, 255, 0.3)',
                     color: '#fff',
                   }}
                   title="View Gallery"
                 >
                   <ImageIcon className="w-[18px] h-[18px]" />
                   <span className="absolute -top-1.5 -right-1.5 text-white font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-md bg-blue-500 text-[10px]" style={{border: '2px solid rgba(255,255,255,0.8)'}}>
                     {(property.imageUrls?.length || 0) + (property.verificationDocUrls?.length || 0) + (property.subleaseConsentDocUrl ? 1 : 0)}
                   </span>
                 </button>
               )}

               <div className="absolute bottom-4 left-4 right-16 z-10">
                 <h3 className="text-white font-bold text-lg truncate drop-shadow-md">
                   {property.title || 'Untitled Property'}
                 </h3>
               </div>
             </div>

             <div className="p-5">
               <div className="flex flex-col gap-3">
                 <div className="flex items-center justify-between">
                   <div className="flex items-center gap-1.5" style={{ color: colors.textSecondary }}>
                     <MapPin className="w-[14px] h-[14px]" />
                     <span className="text-[13px] font-medium truncate">
                       {property.address?.city || 'Unknown'}, {property.address?.region || 'SA'}
                     </span>
                   </div>
                   <div className="text-[11px] font-medium px-2 py-1 rounded-md" style={{ backgroundColor: colors.backgroundSecondary, color: colors.textMuted }}>
                     {property.type || 'Standard'}
                   </div>
                 </div>

                 <div className="flex items-end justify-between mt-1">
                   <div>
                     <p className="text-[11px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: colors.textMuted }}>Monthly Rent</p>
                     <span className="text-lg font-bold" style={{ color: colors.primary }}>
                       {property.monthlyRent?.toLocaleString() || '0'} SAR
                     </span>
                   </div>
                   <div className="text-right">
                     <span className="text-[13px] font-medium" style={{ color: colors.text }}>
                       {property.bedrooms || 0} Bed • {property.bathrooms || 0} Bath
                     </span>
                   </div>
                 </div>
               </div>
               
               <div className="mt-4 pt-3 flex justify-between items-center" style={{ borderTop: `1px dashed ${colors.border}` }}>
                  <span className="text-[11px] font-medium" style={{ color: colors.textMuted }}>
                    Submitted: {property.createdAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || 'Unknown'}
                  </span>
               </div>
             </div>
           </div>
        ))}
      </div>

      {filteredProperties.length === 0 && (
        <div className="flex flex-col items-center justify-center p-12 card-premium" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: colors.backgroundSecondary }}>
             <Building className="w-8 h-8" style={{ color: colors.textMuted }} />
          </div>
          <h3 className="text-[15px] font-semibold" style={{ color: colors.text }}>No properties found</h3>
          <p className="mt-1 text-[13px]" style={{ color: colors.textSecondary }}>
            {filterStatus === 'pending'
              ? "You're all caught up! No pending requests."
              : 'Try adjusting your search terms or filters.'}
          </p>
        </div>
      )}

      {filteredProperties.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={filteredProperties.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      )}

      {selectedProperty && (
        <div className="admin-modal-overlay fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6 lg:p-8 animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.75)', backdropFilter: 'blur(8px)' }}>
            <div
              className="admin-modal-card admin-modal-scroll rounded-2xl max-w-3xl w-full shadow-2xl relative animate-fadeInScale flex flex-col"
              style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
            >
            {/* Modal Header Image */}
            <div className="relative h-64 sm:h-72">
              {selectedProperty.imageUrls && selectedProperty.imageUrls.length > 0 ? (
                <img
                  src={selectedProperty.imageUrls[0]}
                  alt={selectedProperty.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{ backgroundColor: colors.backgroundSecondary }}
                >
                  <Building className="w-16 h-16" style={{ color: colors.textMuted }} />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/60" />
              
              <button
                onClick={() => setSelectedProperty(null)}
                className="absolute top-4 right-4 p-2.5 rounded-full backdrop-blur-md transition-all hover:bg-white/20"
                style={{ backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="absolute top-4 left-4">
                {getStatusBadge(selectedProperty.approvalStatus || 'pending')}
              </div>

              <div className="absolute bottom-6 left-6 right-6">
                 <h2 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-md">
                   {selectedProperty.title || 'Untitled Property'}
                 </h2>
                 {selectedProperty.titleArabic && (
                   <p className="text-lg mt-1 text-white/80 font-medium drop-shadow-sm">
                     {selectedProperty.titleArabic}
                   </p>
                 )}
              </div>
            </div>

            <div className="p-6 sm:p-8">
              {/* Core Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-6 gap-x-4">
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Location</p>
                  <p className="text-[14px] font-medium" style={{ color: colors.text }}>
                    {selectedProperty.address?.city}, {selectedProperty.address?.region}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Type</p>
                  <p className="text-[14px] font-medium capitalize flex items-center gap-1.5" style={{ color: colors.text }}>
                    {selectedProperty.type || 'N/A'}
                  </p>
                </div>
                <div>
                   <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Rental Type</p>
                   <p className="text-[14px] font-medium" style={{ color: colors.text }}>
                     {selectedProperty.rentalType === 'shortTerm' ? 'Short-term' : 'Long-term'}
                   </p>
                </div>
                <div>
                   <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Monthly Rent</p>
                   <p className="text-[15px] font-bold" style={{ color: colors.primary }}>
                     {selectedProperty.monthlyRent?.toLocaleString()} SAR
                   </p>
                </div>
              </div>

              <div className="my-6 border-t" style={{ borderColor: colors.border }} />

              {/* Advanced Characteristics */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                {selectedProperty.type === 'staffHousing' && (
                  <>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Rooms</p>
                      <p className="text-[14px] font-medium" style={{ color: colors.text }}>{selectedProperty.roomsCount || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Max Occ./Room</p>
                      <p className="text-[14px] font-medium" style={{ color: colors.text }}>{selectedProperty.maxOccupantsPerRoom || 'N/A'}</p>
                    </div>
                    {selectedProperty.genderPolicy && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Gender Policy</p>
                        <p className="text-[14px] font-medium" style={{ color: colors.text }}>
                          {selectedProperty.genderPolicy === 'maleOnly' ? 'Male Only' :
                           selectedProperty.genderPolicy === 'femaleOnly' ? 'Female Only' : 'Mixed'}
                        </p>
                      </div>
                    )}
                  </>
                )}

                {selectedProperty.type === 'apartment' && (
                  <>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Capacity</p>
                      <p className="text-[14px] font-medium" style={{ color: colors.text }}>
                         {selectedProperty.bedrooms} Bed • {selectedProperty.bathrooms} Bath
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Floor</p>
                      <p className="text-[14px] font-medium" style={{ color: colors.text }}>Floor {selectedProperty.floorNumber || 'N/A'}</p>
                    </div>
                  </>
                )}

                {!['staffHousing', 'apartment'].includes(selectedProperty.type) && (
                   <div>
                     <p className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: colors.textMuted }}>Capacity</p>
                     <p className="text-[14px] font-medium" style={{ color: colors.text }}>
                        {selectedProperty.bedrooms || 0} Bed • {selectedProperty.bathrooms || 0} Bath
                     </p>
                   </div>
                )}
              </div>

               {/* Amenities Section */}
               {selectedProperty.amenities && Object.keys(selectedProperty.amenities).some(key => selectedProperty.amenities[key]) && (
                <div className="mt-8">
                  <p className="text-[13px] font-semibold mb-3 uppercase tracking-wider" style={{ color: colors.textSecondary }}>Amenities</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(selectedProperty.amenities).map(([key, value]) => {
                      if (!value) return null;
                      const aLabel = { wifi: 'WiFi', parking: 'Parking', airConditioning: 'AC', kitchen: 'Kitchen', pool: 'Pool' }[key] || key;
                      return (
                        <span key={key} className="px-3.5 py-1.5 rounded-full text-[12px] font-medium"
                          style={{ backgroundColor: colors.backgroundSecondary, color: colors.text, border: `1px solid ${colors.border}` }}
                        >
                          {aLabel}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-8">
                <p className="text-[13px] font-semibold mb-2 uppercase tracking-wider" style={{ color: colors.textSecondary }}>Description</p>
                <div className="p-4 rounded-xl text-[14px] leading-relaxed" style={{ backgroundColor: colors.backgroundSecondary, color: colors.text, border: `1px solid ${colors.border}` }}>
                  {selectedProperty.description || 'No description provided'}
                </div>
              </div>

               {/* Images and Documents Gallery View */}
               {(selectedProperty.imageUrls?.length > 0 || selectedProperty.verificationDocUrls?.length > 0 || selectedProperty.subleaseConsentDocUrl) && (
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[13px] font-semibold uppercase tracking-wider" style={{ color: colors.textSecondary }}>Media & Documents</p>
                    <button
                      onClick={() => openImageGallery(selectedProperty)}
                      className="px-3.5 py-1.5 rounded-lg text-[12px] font-semibold transition-all flex items-center gap-1.5 hover:shadow-md"
                      style={{ backgroundColor: colors.primary, color: '#fff' }}
                    >
                      <ImageIcon className="w-3.5 h-3.5" />
                      View All ({(selectedProperty.imageUrls?.length || 0) + (selectedProperty.verificationDocUrls?.length || 0) + (selectedProperty.subleaseConsentDocUrl ? 1 : 0)})
                    </button>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {selectedProperty.imageUrls?.slice(0, 4).map((url, index) => (
                      <div key={index} className="relative aspect-square rounded-xl overflow-hidden shadow-sm group">
                        <img src={url} alt={`Preview ${index + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all cursor-pointer flex items-center justify-center" onClick={() => openImageGallery(selectedProperty)}>
                           <ImageIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedProperty.rejectionReason && (
                <div className="mt-8 p-4 rounded-xl flex gap-3" style={{ backgroundColor: colors.errorGhost, border: `1px solid ${colors.error}40` }}>
                  <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: colors.error }} />
                  <div>
                    <p className="text-[13px] font-bold" style={{ color: colors.error }}>Rejection Reason</p>
                    <p className="text-[14px] mt-1 font-medium" style={{ color: colors.text }}>{selectedProperty.rejectionReason}</p>
                  </div>
                </div>
              )}

              {/* Bottom Action Bar */}
              <div className="sticky bottom-0 -mx-6 sm:-mx-8 p-6 sm:p-8 mt-8 rounded-b-2xl border-t bg-opacity-95 backdrop-blur-sm shadow-[0_-8px_30px_rgba(0,0,0,0.05)]" style={{ borderColor: colors.border, backgroundColor: colors.surface }}>
                <div className="flex flex-col sm:flex-row gap-3">
                  {(selectedProperty.approvalStatus === 'pending' || !selectedProperty.approvalStatus) ? (
                    <>
                      <button
                        onClick={() => approveProperty(selectedProperty.id)}
                        className="btn-primary flex-1 py-3 rounded-xl font-bold text-white shadow-lg flex justify-center items-center gap-2"
                        style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' }}
                      >
                        <CheckCircle className="w-[18px] h-[18px]" /> Approve Listing
                      </button>
                      <button
                        onClick={() => { setPropertyToReject(selectedProperty); setShowRejectModal(true); }}
                        className="flex-1 py-3 rounded-xl font-bold text-white shadow-lg flex justify-center items-center gap-2 transition-transform hover:-translate-y-0.5"
                        style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}
                      >
                        <XCircle className="w-[18px] h-[18px]" /> Reject Request
                      </button>
                    </>
                  ) : selectedProperty.approvalStatus === 'approved' ? (
                    <>
                      <button
                        onClick={() => suspendProperty(selectedProperty.id)}
                        className="flex-1 py-3 rounded-xl font-bold transition-all flex justify-center items-center gap-2"
                        style={{ backgroundColor: colors.warningGhost, color: colors.warning, border: `1px solid ${colors.warning}` }}
                      >
                        <PauseCircle className="w-[18px] h-[18px]" /> Suspend Listing
                      </button>
                    </>
                  ) : selectedProperty.approvalStatus === 'rejected' ? (
                     <button
                        onClick={() => revertToPending(selectedProperty.id)}
                        className="flex-1 py-3 rounded-xl font-bold transition-all flex justify-center items-center gap-2"
                        style={{ backgroundColor: colors.primaryGhost, color: colors.primary, border: `1px solid ${colors.primary}` }}
                      >
                        <Clock className="w-[18px] h-[18px]" /> Revert to Pending
                      </button>
                  ) : selectedProperty.approvalStatus === 'suspended' ? (
                     <button
                        onClick={() => approveProperty(selectedProperty.id)}
                        className="btn-primary flex-1 py-3 rounded-xl font-bold text-white shadow-lg flex justify-center items-center gap-2"
                        style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' }}
                      >
                        <CheckCircle className="w-[18px] h-[18px]" /> Reactivate Listing
                      </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal Overlay */}
      {showRejectModal && (
        <div className="fixed inset-0 flex items-center justify-center z-[60] p-4 animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.8)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-2xl max-w-md w-full p-6 sm:p-8 animate-fadeInScale shadow-2xl" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: colors.errorGhost }}>
               <XCircle className="w-6 h-6" style={{ color: colors.error }} />
            </div>
            <h3 className="text-xl font-bold" style={{ color: colors.text }}>Reject Property</h3>
            <p className="mt-2 text-[14px] leading-relaxed" style={{ color: colors.textSecondary }}>
              Please provide a detailed reason for rejecting this property listing. This will be visible to the host.
            </p>

            <textarea
              className="w-full mt-5 p-3.5 rounded-xl text-[14px] focus:outline-none transition-all"
              style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}`, color: colors.text }}
              rows={4}
              placeholder="e.g. Missing required documents, photos are unclear..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = colors.error; e.target.style.boxShadow = `0 0 0 3px ${colors.errorGhost}`; }}
              onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
            />

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => { setShowRejectModal(false); setPropertyToReject(null); setRejectionReason(''); }}
                className="flex-[1] py-2.5 rounded-xl font-semibold transition-all hover:bg-opacity-80"
                style={{ backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}` }}
              >
                Cancel
              </button>
              <button
                onClick={rejectProperty}
                disabled={!rejectionReason.trim()}
                className="flex-[2] py-2.5 rounded-xl font-bold text-white transition-all hover:opacity-90 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)', boxShadow: '0 4px 14px rgba(239, 68, 68, 0.3)' }}
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* External Image Gallery Modal remains functionally the same but rendered on top */}
      {showImageGallery && galleryProperty && (
        <ImageGallery
          propertyImages={galleryProperty.imageUrls || []}
          verificationDocs={galleryProperty.verificationDocUrls || []}
          subleaseConsentDoc={galleryProperty.subleaseConsentDocUrl}
          propertyTitle={galleryProperty.title || 'Property'}
          onClose={closeImageGallery}
        />
      )}
    </div>
  );
};

export default PropertyRequests;
